import { useState } from "react";
import PropTypes from "prop-types";
import { motion, useReducedMotion } from "framer-motion";
import { Box, ImageOff, MessageCircle } from "lucide-react";
import getMediaUrl from "../tools/utils.jsx";
import { formatPrice, tierLabel } from "./catalogUtils.js";

/**
 * Ficha de un modelo del catálogo (C3D-38): imagen, tipo, nombre, nota y
 * precio por tramos, o «Consultar precio» si el comercial no lo enseña.
 *
 * Las acciones las decide la página: ver en 3D, consultar por WhatsApp y, en
 * el perfil, copiarlo a mis diseños (`extraAction`).
 */
export default function CatalogItemCard({ item, index = 0, onView3d, enquiryUrl, extraAction = null }) {
    const reduceMotion = useReducedMotion();
    // Una miniatura que ya no está en el servidor cae al hueco sin imagen.
    const [imageFailed, setImageFailed] = useState(false);
    const tiers = item.price_tiers || [];

    return (
        <motion.article
            initial={reduceMotion ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.45, delay: Math.min(index, 6) * 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="flex flex-col rounded-xl border border-base-content/10 bg-base-100 overflow-hidden"
        >
            <button
                type="button"
                onClick={item.has_3d ? onView3d : undefined}
                disabled={!item.has_3d}
                aria-label={item.has_3d ? `Ver ${item.title} en 3D` : item.title}
                className="group relative aspect-[4/3] bg-base-200 flex items-center justify-center overflow-hidden disabled:cursor-default"
            >
                {item.image_url && !imageFailed ? (
                    <img
                        onError={() => setImageFailed(true)}
                        src={getMediaUrl(item.image_url)}
                        alt={item.title}
                        loading="lazy"
                        className={`w-full h-full object-contain p-4 transition-transform duration-500 ${item.has_3d ? "group-hover:scale-[1.04]" : ""}`}
                    />
                ) : (
                    <ImageOff className="w-10 h-10 text-base-content/30" aria-hidden="true" />
                )}
            </button>

            <div className="flex flex-col gap-3 p-4 flex-1">
                <div className="flex flex-col gap-1">
                    <span className="text-xs font-medium text-base-content/60">{item.product_label}</span>
                    <h3 className="text-base font-semibold leading-snug text-base-content">{item.title}</h3>
                    {item.note && (
                        <p className="text-sm text-base-content/70 leading-relaxed whitespace-pre-line">{item.note}</p>
                    )}
                </div>

                {item.specs?.length > 0 && (
                    <dl className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-xs">
                        {item.specs.map((spec) => (
                            <div key={spec.label} className="contents">
                                <dt className="text-base-content/60">{spec.label}</dt>
                                <dd className="text-base-content tabular-nums">{spec.value}</dd>
                            </div>
                        ))}
                    </dl>
                )}

                <div className="mt-auto flex flex-col gap-3">
                    {item.show_price && tiers.length > 0 ? (
                        <dl className="flex flex-col gap-1 text-sm">
                            {tiers.map((tier) => (
                                <div key={tier.min_quantity} className="flex items-baseline justify-between gap-3">
                                    <dt className="text-base-content/70">{tierLabel(tier.min_quantity)}</dt>
                                    <dd className="font-semibold tabular-nums text-base-content">
                                        {formatPrice(tier.unit_price)}<span className="font-normal text-base-content/60"> / ud</span>
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    ) : (
                        <p className="text-sm font-semibold text-base-content">Consultar precio</p>
                    )}

                    <div className="flex flex-wrap gap-2">
                        {item.has_3d && (
                            <button type="button" className="btn btn-sm btn-outline gap-2 flex-1" onClick={onView3d}>
                                <Box className="w-4 h-4" aria-hidden="true" /> Ver en 3D
                            </button>
                        )}
                        {enquiryUrl && (
                            <a
                                href={enquiryUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="btn btn-sm btn-primary gap-2 flex-1"
                            >
                                <MessageCircle className="w-4 h-4" aria-hidden="true" /> Consultar
                            </a>
                        )}
                        {extraAction}
                    </div>
                </div>
            </div>
        </motion.article>
    );
}

CatalogItemCard.propTypes = {
    item: PropTypes.shape({
        id: PropTypes.number.isRequired,
        title: PropTypes.string,
        note: PropTypes.string,
        product_label: PropTypes.string,
        image_url: PropTypes.string,
        has_3d: PropTypes.bool,
        specs: PropTypes.arrayOf(PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })),
        show_price: PropTypes.bool,
        price_tiers: PropTypes.arrayOf(PropTypes.shape({
            min_quantity: PropTypes.number,
            unit_price: PropTypes.string,
        })),
    }).isRequired,
    index: PropTypes.number,
    onView3d: PropTypes.func,
    enquiryUrl: PropTypes.string,
    extraAction: PropTypes.node,
};
